// Appointment — bookings table: single-row records with serial numbers,
// date presets, a calendar-style search + filter (status/service), pagination,
// and per-row actions (view / edit / status / cancel). Cancelled rows show
// struck-through with their reason and no actions.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Plus, Search, SearchX, Filter, RotateCcw, X, MoreVertical, Eye, Check, CheckCheck, UserX, XCircle, Pencil, ShieldCheck,
  ChevronLeft, ChevronRight, ExternalLink, CalendarDays,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { LoadErrorState } from '@/components/ListStates';
import {
  appointmentsService, APPT_STATUS_META, to12h, ymd,
  type ApptBooking, type ApptBookingStatus, type ApptService,
} from '@/services/appointments.service';
import FilterCheckList, { type CheckOption } from './FilterCheckList';
import BookingDialog from './BookingDialog';
import BookingDetailDialog from './BookingDetailDialog';
import { ActionMenu } from './Popover';
import { BookingLinkButton } from './BookingLinkButton';
import { useLabels } from '@/contexts/WorkspaceContext';

type Preset = 'all' | 'today' | 'tomorrow' | 'next7' | 'next15' | 'next30';
const PAGE_SIZE = 15;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// future-window presets
const PRESETS: [Preset, string][] = [
  ['all', 'All'],
  ['today', 'Today'],
  ['tomorrow', 'Tomorrow'],
  ['next7', 'Next 7 Days'],
  ['next15', 'Next 15 Days'],
  ['next30', 'Next 30 Days'],
];

const presetRange = (p: Preset): { dateFrom?: string; dateTo?: string } => {
  const now = new Date();
  if (p === 'today') return { dateFrom: ymd(now), dateTo: ymd(now) };
  if (p === 'tomorrow') { const t = addDays(now, 1); return { dateFrom: ymd(t), dateTo: ymd(t) }; }
  if (p === 'next7') return { dateFrom: ymd(now), dateTo: ymd(addDays(now, 7)) };
  if (p === 'next15') return { dateFrom: ymd(now), dateTo: ymd(addDays(now, 15)) };
  if (p === 'next30') return { dateFrom: ymd(now), dateTo: ymd(addDays(now, 30)) };
  return {};
};

const COLS = 'grid-cols-[36px_132px_minmax(0,1.2fr)_120px_minmax(0,1fr)_150px_minmax(0,1fr)_104px_44px]';

// page numbers with ellipsis (e.g. 1 … 4 5 [6] 7 8 … 20)
const pageList = (current: number, total: number): (number | '…')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | '…')[] = [1];
  const from = Math.max(2, current - 1), to = Math.min(total - 1, current + 1);
  if (from > 2) out.push('…');
  for (let i = from; i <= to; i++) out.push(i);
  if (to < total - 1) out.push('…');
  out.push(total);
  return out;
};

const ApptBookingsPage = () => {
  const t = useLabels();
  const [items, setItems] = useState<ApptBooking[]>([]);
  const [services, setServices] = useState<ApptService[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [preset, setPreset] = useState<Preset>('all');
  const [page, setPage] = useState(1);

  // search + filters (calendar style)
  const [query, setQuery] = useState('');
  const [svcFilter, setSvcFilter] = useState<Set<string> | null>(null);
  const [statusFilter, setStatusFilter] = useState<Set<ApptBookingStatus> | null>(null);
  const filterActive = svcFilter !== null || statusFilter !== null;
  const ALL_STATUSES = Object.keys(APPT_STATUS_META) as ApptBookingStatus[];
  const [filterOpen, setFilterOpen] = useState(false);
  const [svcDraft, setSvcDraft] = useState<Set<string>>(new Set());
  const [statusDraft, setStatusDraft] = useState<Set<ApptBookingStatus>>(new Set());
  const openFilters = () => {
    setSvcDraft(svcFilter ? new Set(svcFilter) : new Set(services.map((s) => s.serviceUid)));
    setStatusDraft(statusFilter ? new Set(statusFilter) : new Set(ALL_STATUSES));
    setFilterOpen(true);
  };
  const resetFilters = () => { setSvcDraft(new Set(services.map((s) => s.serviceUid))); setStatusDraft(new Set(ALL_STATUSES)); };
  const applyFilters = () => {
    setSvcFilter(svcDraft.size === services.length ? null : new Set(svcDraft));
    setStatusFilter(statusDraft.size === ALL_STATUSES.length ? null : new Set(statusDraft));
    setFilterOpen(false);
  };
  const appliedFilterCount = (svcFilter ? 1 : 0) + (statusFilter ? 1 : 0);
  const statusOptions: CheckOption[] = ALL_STATUSES.map((st) => ({ value: st, label: APPT_STATUS_META[st].label }));
  const serviceOptions: CheckOption[] = services.map((s) => ({ value: s.serviceUid, label: s.name, subtitle: s.description || `${s.durationMinutes} min`, image: s.imageUrl ?? null }));

  // view / edit / cancel dialogs
  const [bookingOpen, setBookingOpen] = useState(false);
  const [editBooking, setEditBooking] = useState<ApptBooking | null>(null);
  const [viewBooking, setViewBooking] = useState<ApptBooking | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [cancelFor, setCancelFor] = useState<ApptBooking | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Date range + status + service all fetch fresh from the DB. Only the
  // keyword box filters the already-fetched set (instant, no round-trip).
  const load = () => {
    setLoading(true);
    setLoadError(false);
    appointmentsService.listBookings({
      ...presetRange(preset),
      statuses: statusFilter ? Array.from(statusFilter) : undefined,
      serviceUids: svcFilter ? Array.from(svcFilter) : undefined,
    })
      .then((r) => setItems(r.items ?? []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [preset, svcFilter, statusFilter]);
  useEffect(() => {
    appointmentsService.listServices().then((r) => setServices(r.items ?? [])).catch(() => { /* ignore */ });
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((b) =>
      [b.customerName, b.customerPhone, b.customerEmail, b.notes, b.bookingNo, b.serviceName]
        .some((f) => (f || '').toLowerCase().includes(q)),
    );
  }, [items, query]);

  // reset to page 1 whenever the result set changes
  useEffect(() => { setPage(1); }, [preset, query, svcFilter, statusFilter]);
  const anyFilter = preset !== 'all' || !!query.trim() || filterActive;
  const clearAllFilters = () => { setPreset('all'); setQuery(''); setSvcFilter(null); setStatusFilter(null); };
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setSt = async (b: ApptBooking, st: 'CONFIRMED' | 'COMPLETED' | 'NO_SHOW') => {
    try {
      await appointmentsService.setBookingStatus(b.bookingUid, st);
      toast.success(`Marked ${APPT_STATUS_META[st].label}`);
      load();
    } catch (e: any) {
      toast.error(`Couldn't update status — ${e?.response?.data?.message ?? 'Try again.'}`);
    }
  };
  const doCancel = async () => {
    if (!cancelFor) return;
    if (!cancelReason.trim()) { toast.error('A cancellation reason is required'); return; }
    setCancelling(true);
    try {
      await appointmentsService.cancelBooking(cancelFor.bookingUid, cancelReason.trim());
      setCancelFor(null); setCancelReason('');
      toast.success('Booking cancelled');
      load();
    } catch (e: any) {
      toast.error(`Couldn't cancel — ${e?.response?.data?.message ?? 'Try again.'}`);
    }
    setCancelling(false);
  };
  const openView = async (b: ApptBooking) => {
    try {
      const r = await appointmentsService.getBooking(b.bookingUid);
      if (r.item) { setViewBooking(r.item); setViewOpen(true); }
    } catch { /* interceptor toasts */ }
  };
  const openEdit = async (b: ApptBooking) => {
    try {
      const r = await appointmentsService.getBooking(b.bookingUid);
      if (r.item) { setEditBooking(r.item); setBookingOpen(true); }
    } catch { /* interceptor toasts */ }
  };
  const editFromView = () => { if (!viewBooking) return; setViewOpen(false); setEditBooking(viewBooking); setBookingOpen(true); };

  const whenLabel = (b: ApptBooking) =>
    `${new Date(`${b.bookingDate}T00:00:00`).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' })} · ${to12h(b.slotStart)}`;

  return (
    <>
      <Topbar
        title={t('bookings')}
        breadcrumb={`${t('appointments')} / ${t('bookings')}`}
        description="Every booking from every channel — admin, website, WhatsApp."
        right={<BookingLinkButton />}
      />
      <div className="w-full px-7 py-6">
        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex rounded-lg bg-surface-alt p-0.5">
            {PRESETS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setPreset(k)}
                className={clsx('border-none cursor-pointer text-[12px] font-semibold px-3 py-[5px] rounded-md transition-colors whitespace-nowrap',
                  preset === k ? 'bg-surface text-text-primary shadow-soft' : 'bg-transparent text-text-muted hover:text-text-primary')}>
                {label}
              </button>
            ))}
          </div>

          {/* search — 🔍 prefix, filter suffix (right-hand side, calendar style) */}
          <div className="ml-auto flex items-center gap-1.5 bg-surface border border-border rounded-lg pl-2.5 pr-1 h-8 w-[260px]">
            <Search size={14} className="text-text-muted shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, email, notes, no…"
              className="border-none bg-transparent text-[12.5px] flex-1 min-w-0 text-text-primary placeholder:text-text-muted outline-none"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="border-none bg-transparent cursor-pointer text-text-muted hover:text-text-primary p-0.5 shrink-0"><X size={13} /></button>
            )}
            <button type="button" title="Filters" onClick={openFilters} className={clsx(
              'relative w-6 h-6 rounded-md flex items-center justify-center cursor-pointer border-none transition-colors shrink-0',
              filterActive ? 'bg-primary text-on-primary' : 'bg-transparent text-text-muted hover:text-text-primary hover:bg-surface-alt',
            )}>
              <Filter size={13} />
              {filterActive && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-danger ring-2 ring-surface" />}
            </button>
          </div>

          <Button size="sm" iconLeft={<Plus size={15} />} onClick={() => { setEditBooking(null); setBookingOpen(true); }}>
            New booking
          </Button>
        </div>

        {/* table */}
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
          <div className={clsx('grid gap-2 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide text-text-muted border-b border-border bg-surface-alt/50', COLS)}>
            <span>#</span><span>Booking</span><span>Customer</span><span>Phone</span><span>Service</span><span>When</span><span>Notes</span><span>Status</span><span />
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-text-muted"><Loader2 size={17} className="animate-spin" /> Loading bookings…</div>
          ) : loadError ? (
            <LoadErrorState bare what="your bookings" onRetry={load} />
          ) : pageRows.length === 0 && anyFilter ? (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary"><SearchX size={20} /></span>
              <div>
                <p className="text-sm font-semibold text-text-primary">No bookings match these filters</p>
                <p className="mt-1 text-xs text-text-muted">Try another date range, search or filter, or clear them to see every booking.</p>
              </div>
              <Button variant="secondary" size="sm" onClick={clearAllFilters}>Clear filters</Button>
            </div>
          ) : pageRows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary"><CalendarDays size={20} /></span>
              <div>
                <p className="text-sm font-semibold text-text-primary">No bookings yet</p>
                <p className="mx-auto mt-1 max-w-md text-xs text-text-muted">Bookings made by your team, on your booking page or over WhatsApp all land here. Add one yourself, or share your booking link with customers.</p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button size="sm" iconLeft={<Plus size={15} />} onClick={() => { setEditBooking(null); setBookingOpen(true); }}>New booking</Button>
                <BookingLinkButton />
              </div>
            </div>
          ) : pageRows.map((b, i) => {
            const cancelled = b.status === 'CANCELLED';
            const done = b.status === 'COMPLETED' || b.status === 'NO_SHOW';
            return (
              <div key={b.bookingUid} className={clsx('border-b border-border last:border-b-0 hover:bg-surface-alt/40 transition-colors', cancelled && 'bg-red-500/[.03]')}>
                <div
                  onClick={() => void openView(b)}
                  className={clsx('grid gap-2 px-4 py-1 items-center text-[13px] cursor-pointer', COLS, cancelled && 'opacity-70')}
                >
                  <span className="text-[12px] text-text-muted tabular-nums">{(page - 1) * PAGE_SIZE + i + 1}</span>
                  <span className={clsx('whitespace-nowrap text-[12.5px] font-semibold text-primary', cancelled && 'line-through')}>{b.bookingNo}</span>
                  <span className={clsx('truncate text-text-primary', cancelled && 'line-through')}>
                    {b.customerName || '—'}
                    {b.phoneVerified && <ShieldCheck size={12} className="inline ml-1 text-success align-[-1px]" />}
                  </span>
                  <span className={clsx('truncate text-left text-text-muted', cancelled && 'line-through')} dir="ltr">{b.customerPhone ? `+${b.customerPhone.replace(/^\+/, '')}` : '—'}</span>
                  {/* service — hyperlink with a prefix external-link icon */}
                  {b.serviceUid ? (
                    <Link
                      to={`/appointments/services/${b.serviceUid}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="Open service in a new tab"
                      className={clsx('inline-flex items-center gap-1.5 min-w-0 text-primary hover:underline', cancelled && 'line-through')}
                    >
                      <ExternalLink size={12} className="shrink-0" />
                      <span className="truncate">{b.serviceName}</span>
                    </Link>
                  ) : (
                    <span className={clsx('truncate text-text-primary', cancelled && 'line-through')}>{b.serviceName}</span>
                  )}
                  <span className={clsx('truncate text-text-primary whitespace-nowrap', cancelled && 'line-through')}>{whenLabel(b)}</span>
                  <span className={clsx('truncate text-text-muted', cancelled && 'line-through')} title={b.notes ?? ''}>{b.notes || '—'}</span>
                  <span>
                    <span className={clsx('inline-block text-[10.5px] font-semibold rounded-full px-2 py-0.5', APPT_STATUS_META[b.status]?.className)}>
                      {APPT_STATUS_META[b.status]?.label ?? b.status}
                    </span>
                  </span>

                  {cancelled ? (
                    <span />
                  ) : (
                    <ActionMenu
                      trigger={
                        <button type="button" className="h-7 w-7 rounded-lg bg-transparent hover:bg-surface-alt text-text-muted hover:text-text-primary border-none cursor-pointer flex items-center justify-center transition-colors">
                          <MoreVertical size={15} />
                        </button>
                      }
                      items={[
                        { label: 'View details', icon: <Eye size={14} />, onSelect: () => void openView(b) },
                        { label: done ? 'Edit notes' : 'Edit / reschedule', icon: <Pencil size={14} />, onSelect: () => void openEdit(b) },
                        ...(b.status === 'PENDING'
                          ? [{ label: 'Confirm', icon: <Check size={14} className="text-blue-600" />, onSelect: () => void setSt(b, 'CONFIRMED') }]
                          : []),
                        ...(b.status === 'PENDING' || b.status === 'CONFIRMED'
                          ? [
                              { label: 'Mark completed', icon: <CheckCheck size={14} className="text-emerald-600" />, onSelect: () => void setSt(b, 'COMPLETED') },
                              { label: 'No-show', icon: <UserX size={14} className="text-slate-500" />, onSelect: () => void setSt(b, 'NO_SHOW') },
                              { label: 'Cancel booking', icon: <XCircle size={14} />, danger: true, separatorAbove: true, onSelect: () => { setCancelFor(b); setCancelReason(''); } },
                            ]
                          : []),
                      ]}
                    />
                  )}
                </div>

                {/* cancellation reason — full-width strip, spans the whole row */}
                {cancelled && b.cancelReason && (
                  <div className="flex items-start gap-1.5 px-4 pb-1.5 -mt-0.5 text-[11.5px] text-danger">
                    <XCircle size={12} className="shrink-0 mt-[2px]" />
                    <span><b className="font-semibold">{b.cancelledByName || 'Admin'} cancelled:</b> {b.cancelReason}</span>
                  </div>
                )}
              </div>
            );
          })}

          {/* pagination */}
          {!loading && !loadError && filtered.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-border bg-surface-alt/30 text-[12px] text-text-muted">
              <span>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="w-7 h-7 rounded-md border border-border bg-surface text-text-primary cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center hover:bg-surface-alt transition-colors">
                  <ChevronLeft size={14} />
                </button>
                {pageList(page, totalPages).map((n, idx) =>
                  n === '…' ? (
                    <span key={`e${idx}`} className="w-7 h-7 flex items-center justify-center text-text-muted">…</span>
                  ) : (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setPage(n)}
                      className={clsx(
                        'w-7 h-7 rounded-md text-[12px] font-semibold border cursor-pointer transition-colors',
                        n === page ? 'bg-primary text-on-primary border-primary' : 'bg-surface border-border text-text-primary hover:bg-surface-alt',
                      )}
                    >{n}</button>
                  ),
                )}
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="w-7 h-7 rounded-md border border-border bg-surface text-text-primary cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center hover:bg-surface-alt transition-colors">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* filter dialog */}
      <Modal open={filterOpen} onClose={() => setFilterOpen(false)} title={`Filters${appliedFilterCount > 0 ? ` (${appliedFilterCount})` : ''}`}>
        <div className="space-y-4 py-1 min-w-0">
          <FilterCheckList label="Status" options={statusOptions} selected={statusDraft} onChange={(n) => setStatusDraft(n as Set<ApptBookingStatus>)} searchPlaceholder="Search status…" />
          <FilterCheckList label="Service" options={serviceOptions} selected={svcDraft} onChange={setSvcDraft} withImage searchPlaceholder="Search service…" />
        </div>
        <div className="flex items-center justify-between gap-2 pt-4 mt-2 border-t border-border">
          <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} onClick={resetFilters}>Reset</Button>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setFilterOpen(false)}>Cancel</Button>
            <Button size="sm" iconLeft={<Filter size={14} />} onClick={applyFilters}>Apply</Button>
          </div>
        </div>
      </Modal>

      <BookingDialog open={bookingOpen} onClose={() => setBookingOpen(false)} onSaved={load} services={services} booking={editBooking} />
      <BookingDetailDialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        booking={viewBooking}
        service={services.find((s) => s.serviceUid === viewBooking?.serviceUid) ?? null}
        onEdit={editFromView}
        onChanged={load}
      />

      {/* cancel with reason */}
      <Modal open={!!cancelFor} onClose={() => setCancelFor(null)} title={`Cancel ${cancelFor?.bookingNo ?? ''}?`} widthClass="max-w-[420px]">
        <div className="flex flex-col gap-3">
          <div className="text-[13px] text-text-muted">
            {cancelFor?.customerName || cancelFor?.customerPhone} · {cancelFor?.serviceName} · {cancelFor && to12h(cancelFor.slotStart)} on {cancelFor?.bookingDate}
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Reason (required)</label>
            <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Customer asked to cancel" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCancelFor(null)}>Keep booking</Button>
            <Button variant="danger" onClick={doCancel} loading={cancelling}>
              Cancel booking
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ApptBookingsPage;
